/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-01-05 10:30:50
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { InlineError, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { sortableContainer, sortableElement } from 'react-sortable-hoc';
import { RenderLabelTooltip } from './render-label-tooltip';
import { selectStyle } from './render-select.style';
// DONE
// Custom multiValue
const SortableElement = sortableElement(components.MultiValue);
const SortableElementWrapper = props => {
  const { value } = props.data;
  const { input } = props.selectProps;
  const index = _.findIndex(input.value, ['value', value]);

  return <SortableElement key={`item-${index}`} index={index} {...props} />;
};

// Custom control
const SortableContainer = sortableContainer(components.Control);
const SortableContainerWrapper = props => {
  const reorder = (list: any[], startIndex: number, endIndex: number): any[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const { input } = props.selectProps;
    const value = reorder(input.value, oldIndex, newIndex);
    input.onChange(value);
  };

  return (
    <SortableContainer
      // react-sortable-hoc props:
      axis="xy"
      onSortEnd={onSortEnd}
      distance={4}
      getHelperDimensions={({ node }) => node.getBoundingClientRect()}
      helperClass="sortableHelper"
      {...props}
    />
  );
};

const MultiValueContainer = props => {
  const { inputRef } = props.selectProps;
  return (
    <div
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
        if (inputRef.state.menuIsOpen) {
          inputRef.select.blur();
        }
      }}>
      <components.MultiValueContainer {...props} />
    </div>
  );
};

class RenderSelectMultipleComponent extends Component<WithI18nProps> {
  render() {
    const { meta, options, label, tooltip, input, placeholder, isDisabled, textHelper, i18n } =
      this.props;

    const hasError = !!meta.error && !!meta.touched;

    return (
      <div className={hasError ? 'Polaris-TextField--error' : ''}>
        <RenderLabelTooltip label={label} tooltip={tooltip} />

        <AsyncPaginate
          id={input.name}
          // react-select props:
          selectRef={input => (this.selectRef = input)}
          isMulti
          closeMenuOnSelect={false}
          loadOptions={this.loadOptions}
          cacheUniqs={options}
          value={input.value}
          onChange={value => {
            input.onChange(value || []);
          }}
          label={label}
          styles={selectStyle}
          placeholder={placeholder}
          components={{
            MultiValue: SortableElementWrapper,
            Control: SortableContainerWrapper,
            MultiValueContainer
          }}
          menuPortalTarget={document.body}
          maxMenuHeight={150}
          classNamePrefix="BC-Select"
          isDisabled={isDisabled ? true : false}
          // Pass input value to select as a props
          input={input}
          inputRef={this.selectRef}
          noOptionsMessage={() => i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')}
        />

        {textHelper ? (
          <p style={{ marginTop: '0.25rem' }}>
            <TextStyle variation="subdued">{textHelper}</TextStyle>
          </p>
        ) : (
          ''
        )}
        {meta.error ? <InlineError message={meta.error} /> : ''}
      </div>
    );
  }

  // Wait ms time
  sleep = ms =>
    new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, ms);
    });

  // Pagination options
  loadOptions = async (search, prevOptions) => {
    const { options } = this.props;
    if (!_.isEmpty(prevOptions)) {
      await this.sleep(1000);
    }

    let filteredOptions;
    if (!search) {
      filteredOptions = options;
    } else {
      const searchLower = search.toLowerCase();

      filteredOptions = options.filter(({ label }) => label.toLowerCase().includes(searchLower));
    }

    const hasMore = filteredOptions.length > prevOptions.length + 20;
    const slicedOptions = filteredOptions.slice(prevOptions.length, prevOptions.length + 20);

    return {
      options: slicedOptions,
      hasMore
    };
  };
}

export const RenderSelectMultiple = withI18n()(RenderSelectMultipleComponent);
